'use client';

/**
 * Minimal Cloudflare Turnstile client hook (invisible widget).
 *
 * Loads the Turnstile script once, renders one invisible widget, and exposes
 * `getToken(action)` returning a fresh single-use token to attach to the
 * OTP-send request. Tokens are single-use and expire in 5 minutes, so every
 * call resets the widget and executes a new challenge.
 *
 * When `NEXT_PUBLIC_TURNSTILE_SITE_KEY` is unset (local dev / not rolled out),
 * `getToken` resolves to null and the server treats verification as disabled.
 *
 * The page renders `<div ref={containerRef} />` somewhere in the form; the
 * invisible widget needs a mount point but shows no UI unless Cloudflare
 * decides an interactive challenge is required. Works inside the Capacitor
 * WebView because it runs on the remote (allow-listed) origin.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 8000;

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  execute: (idOrEl: string | HTMLElement, opts?: Record<string, unknown>) => void;
  reset: (id?: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')));
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile script failed'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function useTurnstile() {
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const resolverRef = useRef<((token: string | null) => void) | null>(null);
  // True while a challenge is running. The form swaps its submit button for the
  // widget while this is set, so an interactive challenge appears exactly where
  // the button was instead of floating elsewhere on the page.
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current || widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          // Turnstile has no "invisible" size (that's a reCAPTCHA-ism and throws).
          // interaction-only keeps it hidden unless a challenge is needed, and
          // execution:'execute' runs it only when we call execute() below.
          appearance: 'interaction-only',
          execution: 'execute',
          callback: (token: string) => {
            resolverRef.current?.(token);
            resolverRef.current = null;
            setIsVerifying(false);
          },
          'error-callback': () => {
            resolverRef.current?.(null);
            resolverRef.current = null;
            setIsVerifying(false);
          },
          // A token is only valid for 300s; drop the stale one so the next
          // submit runs a fresh challenge rather than sending an expired token.
          'expired-callback': () => {
            resolverRef.current?.(null);
            resolverRef.current = null;
            setIsVerifying(false);
          },
        });
      })
      .catch(() => {
        /* script load failure -> getToken resolves null, server fails open */
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  const getToken = useCallback(
    async (action?: string): Promise<string | null> => {
      // Disabled (no site key) or widget not ready — server treats a null token
      // as "no token" (no-op in monitor mode, blocked only under enforce).
      if (!siteKey || !window.turnstile || !widgetIdRef.current) return null;

      setIsVerifying(true);
      return new Promise<string | null>((resolve) => {
        resolverRef.current = resolve;
        try {
          window.turnstile!.reset(widgetIdRef.current!);
          window.turnstile!.execute(widgetIdRef.current!, action ? { action } : undefined);
        } catch {
          resolverRef.current = null;
          setIsVerifying(false);
          resolve(null);
          return;
        }
        // Safety timeout so a stuck challenge never hangs the login form.
        setTimeout(() => {
          if (resolverRef.current === resolve) {
            resolverRef.current = null;
            setIsVerifying(false);
            resolve(null);
          }
        }, TOKEN_TIMEOUT_MS);
      });
    },
    [siteKey]
  );

  return { getToken, containerRef, isVerifying };
}
